const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const getHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

// Helper functions for API calls
const get = async (endpoint, params = {}) => {
  const queryString = Object.keys(params).length 
    ? '?' + new URLSearchParams(params).toString() 
    : '';
  const res = await fetch(`${API_BASE}/api${endpoint}${queryString}`, {
    method: 'GET',
    headers: getHeaders()
  });
  if (!res.ok) {
    const error = await res.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || 'Request failed');
  }
  return res.json();
};

const post = async (endpoint, data = {}) => {
  const res = await fetch(`${API_BASE}/api${endpoint}`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(data)
  });
  if (!res.ok) {
    const error = await res.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || 'Request failed');
  }
  return res.json();
};

const put = async (endpoint, data = {}) => {
  const res = await fetch(`${API_BASE}/api${endpoint}`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify(data)
  });
  if (!res.ok) {
    const error = await res.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || 'Request failed');
  }
  return res.json();
};

const del = async (endpoint) => {
  const res = await fetch(`${API_BASE}/api${endpoint}`, {
    method: 'DELETE',
    headers: getHeaders()
  });
  if (!res.ok) {
    const error = await res.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || 'Request failed');
  }
  return res.json();
};

export const api = {
  // Auth
  login: async (email, password) => {
    const res = await fetch(`${API_BASE}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    if (!res.ok) throw new Error('Login failed');
    return res.json();
  },

  register: async (email, password, full_name, role) => {
    const res = await fetch(`${API_BASE}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, full_name, role })
    });
    if (!res.ok) throw new Error('Registration failed');
    return res.json();
  },

  forgotPassword: async (email) => {
    const res = await fetch(`${API_BASE}/api/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    });
    if (!res.ok) throw new Error('Failed to request password reset');
    return res.json();
  },

  resetPassword: async (token, new_password) => {
    const res = await fetch(`${API_BASE}/api/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, new_password })
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.detail || 'Failed to reset password');
    }
    return res.json();
  },

  // Users (Admin only)
  getUsers: async () => {
    const res = await fetch(`${API_BASE}/api/users`, {
      headers: getHeaders()
    });
    if (!res.ok) throw new Error('Failed to fetch users');
    return res.json();
  },

  deleteUser: async (email) => {
    const res = await fetch(`${API_BASE}/api/users/${encodeURIComponent(email)}`, {
      method: 'DELETE',
      headers: getHeaders()
    });
    if (!res.ok) throw new Error('Failed to delete user');
    return res.json();
  },

  // Entities
  getEntities: async () => {
    const res = await fetch(`${API_BASE}/api/entities`, {
      headers: getHeaders()
    });
    if (!res.ok) throw new Error('Failed to fetch entities');
    return res.json();
  },

  getEntity: async (entityId) => {
    const res = await fetch(`${API_BASE}/api/entities/${entityId}`, {
      headers: getHeaders()
    });
    if (!res.ok) throw new Error('Failed to fetch entity');
    return res.json();
  },

  createEntity: async (entityData) => {
    const res = await fetch(`${API_BASE}/api/entities`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(entityData)
    });
    if (!res.ok) throw new Error('Failed to create entity');
    return res.json();
  },

  updateEntity: async (entityId, updates) => {
    const res = await fetch(`${API_BASE}/api/entities/${entityId}`, {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify(updates)
    });
    if (!res.ok) throw new Error('Failed to update entity');
    return res.json();
  },

  deleteEntity: async (entityId) => {
    const res = await fetch(`${API_BASE}/api/entities/${entityId}`, {
      method: 'DELETE',
      headers: getHeaders()
    });
    if (!res.ok) throw new Error('Failed to delete entity');
    return res.json();
  },

  // State Requirements
  getStateRequirements: async (state = null) => {
    const url = state ? `${API_BASE}/api/states?state=${state}` : `${API_BASE}/api/states`;
    const res = await fetch(url, { headers: getHeaders() });
    if (!res.ok) throw new Error('Failed to fetch state requirements');
    return res.json();
  },

  createStateRequirement: async (data) => {
    const res = await fetch(`${API_BASE}/api/states`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to create state requirement');
    return res.json();
  },

  updateStateRequirement: async (reqId, updates) => {
    const res = await fetch(`${API_BASE}/api/states/${reqId}`, {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify(updates)
    });
    if (!res.ok) throw new Error('Failed to update state requirement');
    return res.json();
  },

  // Licenses
  getLicenses: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    const res = await fetch(`${API_BASE}/api/licenses?${params}`, {
      headers: getHeaders()
    });
    if (!res.ok) throw new Error('Failed to fetch licenses');
    return res.json();
  },

  createLicense: async (data) => {
    const res = await fetch(`${API_BASE}/api/licenses`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to create license');
    return res.json();
  },

  updateLicense: async (licenseId, updates) => {
    const res = await fetch(`${API_BASE}/api/licenses/${licenseId}`, {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify(updates)
    });
    if (!res.ok) throw new Error('Failed to update license');
    return res.json();
  },

  // DBAs
  getDbas: async () => {
    const res = await fetch(`${API_BASE}/api/dbas`, {
      headers: getHeaders()
    });
    if (!res.ok) throw new Error('Failed to fetch DBAs');
    return res.json();
  },

  createDba: async (data) => {
    const res = await fetch(`${API_BASE}/api/dbas`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to create DBA');
    return res.json();
  },

  updateDba: async (dbaId, updates) => {
    const res = await fetch(`${API_BASE}/api/dbas/${dbaId}`, {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify(updates)
    });
    if (!res.ok) throw new Error('Failed to update DBA');
    return res.json();
  },

  deleteDba: async (dbaId) => {
    const res = await fetch(`${API_BASE}/api/dbas/${dbaId}`, {
      method: 'DELETE',
      headers: getHeaders()
    });
    if (!res.ok) throw new Error('Failed to delete DBA');
    return res.json();
  },


  deleteLicense: async (licenseId) => {
    const res = await fetch(`${API_BASE}/api/licenses/${licenseId}`, {
      method: 'DELETE',
      headers: getHeaders()
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ detail: 'Failed to delete license' }));
      throw new Error(error.detail || error.message || 'Failed to delete license');
    }
    return res.json();
  },

  // DBAs
  getDBAs: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    const res = await fetch(`${API_BASE}/api/dbas?${params}`, {
      headers: getHeaders()
    });
    if (!res.ok) throw new Error('Failed to fetch DBAs');
    return res.json();
  },

  createDBA: async (data) => {
    const res = await fetch(`${API_BASE}/api/dbas`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to create DBA');
    return res.json();
  },

  updateDBA: async (dbaId, updates) => {
    const res = await fetch(`${API_BASE}/api/dbas/${dbaId}`, {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify(updates)
    });
    if (!res.ok) throw new Error('Failed to update DBA');
    return res.json();
  },

  deleteDBA: async (dbaId) => {
    const res = await fetch(`${API_BASE}/api/dbas/${dbaId}`, {
      method: 'DELETE',
      headers: getHeaders()
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ detail: 'Failed to delete DBA' }));
      throw new Error(error.detail || error.message || 'Failed to delete DBA');
    }
    return res.json();
  },

  // Alias for backward compatibility (registrations are DBAs)
  deleteRegistration: async (dbaId) => {
    const res = await fetch(`${API_BASE}/api/dbas/${dbaId}`, {
      method: 'DELETE',
      headers: getHeaders()
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ detail: 'Failed to delete registration' }));
      throw new Error(error.detail || error.message || 'Failed to delete registration');
    }
    return res.json();
  },

  // Filings
  getFilings: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    const res = await fetch(`${API_BASE}/api/filings?${params}`, {
      headers: getHeaders()
    });
    if (!res.ok) throw new Error('Failed to fetch filings');
    return res.json();
  },

  getFiling: async (filingId) => {
    const res = await fetch(`${API_BASE}/api/filings/${filingId}`, {
      headers: getHeaders()
    });
    if (!res.ok) throw new Error('Failed to fetch filing');
    return res.json();
  },

  updateFiling: async (filingId, updates) => {
    const res = await fetch(`${API_BASE}/api/filings/${filingId}`, {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify(updates)
    });
    if (!res.ok) throw new Error('Failed to update filing');
    return res.json();
  },

  deleteFiling: async (filingId) => {
    const res = await fetch(`${API_BASE}/api/filings/${filingId}`, {
      method: 'DELETE',
      headers: getHeaders()
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ detail: 'Failed to delete filing' }));
      throw new Error(error.detail || error.message || 'Failed to delete filing');
    }
    return res.json();
  },

  // Tasks
  getTasks: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    const res = await fetch(`${API_BASE}/api/tasks?${params}`, {
      headers: getHeaders()
    });
    if (!res.ok) throw new Error('Failed to fetch tasks');
    return res.json();
  },

  createTask: async (taskData) => {
    const res = await fetch(`${API_BASE}/api/tasks`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(taskData)
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error('Task creation failed:', errorText);
      throw new Error(`Failed to create task: ${res.status} ${errorText}`);
    }
    return res.json();
  },

  updateTask: async (taskId, updates) => {
    const res = await fetch(`${API_BASE}/api/tasks/${taskId}`, {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify(updates)
    });
    if (!res.ok) throw new Error('Failed to update task');
    return res.json();
  },

  // Documents
  getDocuments: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    const res = await fetch(`${API_BASE}/api/documents?${params}`, {
      headers: getHeaders()
    });
    if (!res.ok) throw new Error('Failed to fetch documents');
    return res.json();
  },

  uploadDocument: async (formData) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_BASE}/api/documents/upload`, {
      method: 'POST',
      headers: { ...(token && { 'Authorization': `Bearer ${token}` }) },
      body: formData
    });
    if (!res.ok) throw new Error('Failed to upload document');
    return res.json();
  },

  updateDocument: async (documentId, data) => {
    const res = await fetch(`${API_BASE}/api/documents/${documentId}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to update document');
    return res.json();
  },

  deleteDocument: async (documentId) => {
    const res = await fetch(`${API_BASE}/api/documents/${documentId}`, {
      method: 'DELETE',
      headers: getHeaders()
    });
    if (!res.ok) throw new Error('Failed to delete document');
    return res.json();
  },

  downloadDocument: (documentId) => {
    return `${API_BASE}/api/documents/${documentId}/download`;
  },

  // Dashboard
  getDashboardStats: async () => {
    const res = await fetch(`${API_BASE}/api/dashboard/stats`, {
      headers: getHeaders()
    });
    if (!res.ok) throw new Error('Failed to fetch dashboard stats');
    return res.json();
  },

  getDueItems: async (bucket = '30') => {
    const res = await fetch(`${API_BASE}/api/dashboard/due-items?bucket=${bucket}`, {
      headers: getHeaders()
    });
    if (!res.ok) throw new Error('Failed to fetch due items');
    return res.json();
  },

  getRecentActivity: async (limit = 20) => {
    const res = await fetch(`${API_BASE}/api/dashboard/recent-activity?limit=${limit}`, {
      headers: getHeaders()
    });
    if (!res.ok) throw new Error('Failed to fetch recent activity');
    return res.json();
  },

  // State Compliance (Single Source of Truth)
  getStateCompliance: async () => {
    const res = await fetch(`${API_BASE}/api/state-compliance`, {
      headers: getHeaders()
    });
    if (!res.ok) throw new Error('Failed to fetch state compliance data');
    return res.json();
  },


  // Search
  search: async (query) => {
    const res = await fetch(`${API_BASE}/api/search?q=${encodeURIComponent(query)}`, {
      headers: getHeaders()
    });
    if (!res.ok) throw new Error('Search failed');
    return res.json();
  },

  // Audit
  getAuditLog: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    const res = await fetch(`${API_BASE}/api/audit?${params}`, {
      headers: getHeaders()
    });
    if (!res.ok) throw new Error('Failed to fetch audit log');
    return res.json();
  },

  // Expenses
  getExpenses: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    const res = await fetch(`${API_BASE}/api/expenses?${params}`, {
      headers: getHeaders()
    });
    if (!res.ok) throw new Error('Failed to fetch expenses');
    return res.json();
  },

  createExpense: async (expenseData) => {
    const res = await fetch(`${API_BASE}/api/expenses`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(expenseData)
    });
    if (!res.ok) throw new Error('Failed to create expense');
    return res.json();
  },

  updateExpense: async (expenseId, updates) => {
    const res = await fetch(`${API_BASE}/api/expenses/${expenseId}`, {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify(updates)
    });
    if (!res.ok) throw new Error('Failed to update expense');
    return res.json();
  },

  deleteExpense: async (expenseId) => {
    const res = await fetch(`${API_BASE}/api/expenses/${expenseId}`, {
      method: 'DELETE',
      headers: getHeaders()
    });
    if (!res.ok) throw new Error('Failed to delete expense');
    return res.json();
  },

  getExpenseSummary: async () => {
    const res = await fetch(`${API_BASE}/api/expenses/analytics/summary`, {
      headers: getHeaders()
    });
    if (!res.ok) throw new Error('Failed to fetch expense summary');
    return res.json();
  },

  getExpenseOverview: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    const res = await fetch(`${API_BASE}/api/expenses/stats/overview?${params}`, {
      headers: getHeaders()
    });
    if (!res.ok) throw new Error('Failed to fetch expense overview');
    return res.json();
  },

  // Expense Prompts
  getExpensePrompts: async (status = 'pending') => {
    const res = await fetch(`${API_BASE}/api/expense-prompts?status=${status}`, {
      headers: getHeaders()
    });
    if (!res.ok) throw new Error('Failed to fetch expense prompts');
    return res.json();
  },

  // Get Expense Tracker Overview Cards (State Matrix Integrated)
  getExpenseTrackerOverviewCards: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);
    
    const res = await fetch(`${API_BASE}/api/expense-tracker/overview-cards?${params}`, {
      headers: getHeaders()
    });
    if (!res.ok) throw new Error('Failed to fetch expense tracker overview cards');
    return res.json();
  },

  updateExpensePrompt: async (promptId, updates) => {
    const res = await fetch(`${API_BASE}/api/expense-prompts/${promptId}`, {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify(updates)
    });
    if (!res.ok) throw new Error('Failed to update expense prompt');
    return res.json();
  },

  deleteExpensePrompt: async (promptId) => {
    const res = await fetch(`${API_BASE}/api/expense-prompts/${promptId}`, {
      method: 'DELETE',
      headers: getHeaders()
    });
    if (!res.ok) throw new Error('Failed to delete expense prompt');
    return res.json();
  },

  getExpensesByState: async () => {
    const res = await fetch(`${API_BASE}/api/expenses/analytics/by-state`, {
      headers: getHeaders()
    });
    if (!res.ok) throw new Error('Failed to fetch expenses by state');
    return res.json();
  },

  getExpensesByCategory: async () => {
    const res = await fetch(`${API_BASE}/api/expenses/analytics/by-category`, {
      headers: getHeaders()
    });
    if (!res.ok) throw new Error('Failed to fetch expenses by category');
    return res.json();
  },

  // Expense Invoice Management
  uploadInvoice: async (expenseId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const res = await fetch(`${API_BASE}/api/expenses/${expenseId}/upload-invoice`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: formData
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ detail: 'Failed to upload invoice' }));
      throw new Error(error.detail || 'Failed to upload invoice');
    }
    return res.json();
  },

  downloadInvoice: (expenseId, filename) => {
    return `${API_BASE}/api/expenses/${expenseId}/invoice/${filename}`;
  },

  deleteInvoice: async (expenseId) => {
    const res = await fetch(`${API_BASE}/api/expenses/${expenseId}/invoice`, {
      method: 'DELETE',
      headers: getHeaders()
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ detail: 'Failed to delete invoice' }));
      throw new Error(error.detail || 'Failed to delete invoice');
    }
    return res.json();
  },

  // Export Expenses
  exportExpensesCSV: (filters = {}) => {
    const params = new URLSearchParams(filters);
    return `${API_BASE}/api/export/expenses?${params}`;
  },

  // Registered Agents Management
  getRAOverview: () => get('/registered-agents/overview'),
  getRAProviders: () => get('/registered-agents/providers'),
  getRAProvider: (providerId) => get(`/registered-agents/providers/${providerId}`),
  createRAProvider: (data) => post('/registered-agents/providers', data),
  updateRAProvider: (providerId, data) => put(`/registered-agents/providers/${providerId}`, data),
  deleteRAProvider: (providerId) => del(`/registered-agents/providers/${providerId}`),
  revealRACredential: (providerId, field) => post(`/registered-agents/providers/${providerId}/reveal-credential?field=${field}`),
  getRAStateAssignments: (filters = {}) => get('/registered-agents/states', filters),
  createRAStateAssignment: (data) => post('/registered-agents/states', data),
  updateRAStateAssignment: (assignmentId, data) => put(`/registered-agents/states/${assignmentId}`, data),
  deleteRAStateAssignment: (assignmentId) => del(`/registered-agents/states/${assignmentId}`),
  migrateRAFromEntities: () => post('/registered-agents/migrate-from-entities')
};

export default api;
